import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart, HandHeart, Car, Home as HomeIcon, ArrowRight, HandHelping, Backpack, Sparkles, Quote } from "lucide-react";
import DonateDialog from "@/components/DonateDialog";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { IMG, PROGRAMS, IMPACT_ANCHORS, MINISTRY } from "@/lib/content";
import { getImpact, getTestimonials } from "@/lib/api";

const WAYS = [
  { icon: Heart, title: "Give Financially", desc: "Fund mentorship, outreach, and the foster care home restoration.", cta: "donate" },
  { icon: HandHelping, title: "Volunteer Your Skills", desc: "Trades, grant writing, mentoring, events — your gifts matter.", to: "/get-involved" },
  { icon: Car, title: "Donate a Vehicle", desc: "A reliable car can change a young woman's entire future.", to: "/get-involved" },
  { icon: HomeIcon, title: "Give Homes & Goods", desc: "Property, furniture, backpacks, and household essentials.", to: "/get-involved" },
];

function Counter({ value, label }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let started = false;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started) {
        started = true;
        const dur = 1400, start = performance.now();
        const tick = (t) => {
          const p = Math.min((t - start) / dur, 1);
          setN(Math.floor((1 - Math.pow(1 - p, 3)) * value));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [value]);
  return (
    <div ref={ref} className="text-center" data-testid="impact-counter">
      <div className="font-heading text-4xl sm:text-5xl font-semibold text-gold">{n.toLocaleString()}+</div>
      <div className="text-sm text-cream/70 mt-1">{label}</div>
    </div>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.75]);

  const [impact, setImpact] = useState(null);
  const [tItems, setTItems] = useState([]);
  useEffect(() => {
    getImpact().then(setImpact).catch(() => {});
    getTestimonials().then((d) => setTItems(d.slice(0, 1))).catch(() => {});
  }, []);

  return (
    <>
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={IMG.hero} alt="Pastor Jewel Jones ministering in worship" className="w-full h-full object-cover object-top" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-plum-dark" />
        <div className="absolute inset-0 bg-gradient-to-t from-plum-dark via-plum-dark/30 to-transparent" />

        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-20 sm:pb-28">
          <motion.span
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="text-gold-light text-sm tracking-[0.24em] uppercase font-semibold mb-5"
          >
            Greater Faith International Ministries
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading text-cream text-4xl sm:text-6xl lg:text-7xl font-semibold leading-[0.98] max-w-4xl text-balance"
          >
            Faith in action. <span className="text-gold">Love</span> in motion.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="text-cream/85 text-lg sm:text-xl max-w-2xl mt-6 leading-relaxed"
          >
            We mentor young women, feed families, restore homes for youth, and meet real needs across Norfolk. Your partnership turns compassion into change.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-wrap gap-4 mt-9"
          >
            <DonateDialog
              trigger={
                <button data-testid="hero-donate-btn" className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-8 py-4 text-base flex items-center gap-2 transition-all hover:-translate-y-1 shadow-lg">
                  <Heart size={18} /> Donate Now
                </button>
              }
            />
            <Link to="/get-involved" data-testid="hero-involved-btn" className="bg-cream/10 border border-cream/30 text-cream font-medium rounded-full px-8 py-4 text-base flex items-center gap-2 hover:bg-cream/20 transition-colors backdrop-blur-sm">
              Volunteer & Give Goods <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* IMPACT BAR */}
      <section className="bg-plum py-14 relative z-10" data-testid="impact-bar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10">
          <Counter value={impact?.families_served ?? 250} label="Families Served" />
          <Counter value={impact?.backpacks_given ?? 100} label="Backpacks Given" />
          <Counter value={impact?.women_mentored ?? 50} label="Women Mentored" />
          <Counter value={impact?.years_serving ?? 10} label="Years of Service" />
        </div>
      </section>

      {/* WELCOME / MISSION */}
      <section className="py-24 sm:py-32 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="img-zoom rounded-2xl shadow-xl relative">
            <img src={IMG.group} alt="Greater Faith congregation" className="w-full h-[440px] object-cover rounded-2xl" />
            <div className="absolute -bottom-6 -right-4 sm:right-6 bg-cream rounded-2xl shadow-xl px-6 py-5 border border-plum/10 max-w-[240px]">
              <Sparkles className="text-gold-dark mb-2" size={22} />
              <p className="text-sm text-plum font-medium leading-snug">A home for the whole community &mdash; every heart welcome.</p>
            </div>
          </Reveal>
          <div>
            <SectionHeading
              kicker="Who We Are"
              title="A ministry that shows up where it matters most"
              subtitle="Led by Pastor Jewel Jones, Greater Faith International Ministries exists to love God and love people through tangible acts of service. From the classroom to the kitchen table to a home under restoration, we bring faith into the everyday."
            />
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/about" className="text-plum font-medium border-b-2 border-gold pb-1 flex items-center gap-2 hover:gap-3 transition-all" data-testid="home-about-link">
                Meet Pastor Jewel <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NEW CHURCH HOME */}
      <section className="py-24 sm:py-32 bg-ink text-cream relative z-10 overflow-hidden" data-testid="new-church">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal className="img-zoom rounded-2xl shadow-2xl order-2 lg:order-1">
              <img src={IMG.newChurchSanctuary} alt="Greater Faith's new sanctuary" className="w-full h-[440px] object-cover rounded-2xl" />
            </Reveal>
            <div className="order-1 lg:order-2">
              <span className="text-gold-light text-sm tracking-[0.22em] uppercase font-semibold">A New Season</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 leading-tight">
                Our new church home is open
              </h2>
              <p className="text-cream/75 text-lg mt-6 leading-relaxed">
                By faith and the hands of many, an old space was transformed into a beautiful sanctuary. We've moved to a new location and thrown open our doors with a Grand Opening &amp; Church Warming Celebration &mdash; and there's a seat here for you.
              </p>
              <div className="grid grid-cols-3 gap-3 mt-8">
                <div className="img-zoom rounded-xl h-28"><img src={IMG.churchExterior} alt="New church exterior" className="w-full h-full object-cover rounded-xl" /></div>
                <div className="img-zoom rounded-xl h-28"><img src={IMG.ncFraming} alt="Renovation in progress" className="w-full h-full object-cover rounded-xl" /></div>
                <div className="img-zoom rounded-xl h-28"><img src={IMG.ncCarpet} alt="Restoration work" className="w-full h-full object-cover rounded-xl" /></div>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <DonateDialog trigger={
                  <button data-testid="newchurch-donate-btn" className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-7 py-3.5 flex items-center gap-2 transition-all hover:-translate-y-1">
                    <Heart size={17} /> Support Our New Home
                  </button>
                } />
                <div className="text-cream/80 text-sm">
                  <span className="block text-gold-light font-semibold">Give via Cash App</span>
                  <span className="font-heading text-2xl text-cream">{MINISTRY.cashApp}</span>
                </div>
              </div>
              <p className="text-cream/50 text-xs mt-3">You can also bless us with church &amp; household items. Thank you for your continued love and support.</p>
            </div>
          </div>

          {/* From restoration to worship */}
          <div className="mt-14">
            <Reveal className="flex items-center gap-3 mb-6">
              <Sparkles className="text-gold" size={20} />
              <h3 className="font-heading text-2xl sm:text-3xl text-cream font-semibold">From restoration to worship</h3>
            </Reveal>
            <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { src: IMG.ncMinister, cap: "Ministering the Word together" },
                { src: IMG.ncChoir, cap: "Our youth choir at the new home" },
                { src: IMG.ncFlooring, cap: "New flooring delivered" },
                { src: IMG.ncFloorLay, cap: "Laying the new floor by hand" },
              ].map((it, i) => (
                <StaggerItem key={i}>
                  <div className="img-zoom rounded-2xl shadow-lg h-56 relative">
                    <img src={it.src} alt={it.cap} className="w-full h-full object-cover rounded-2xl" />
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/90 to-transparent p-4 rounded-b-2xl">
                      <p className="text-cream text-xs sm:text-sm font-medium">{it.cap}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>


      {/* PROGRAMS BENTO */}
      <section className="py-20 sm:py-28 bg-white relative z-10" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading kicker="Our Programs" title="Stories being written every day" subtitle="Each program is a chance to partner — with your time, your gifts, and your heart." />
          <Stagger className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[300px]">
            {PROGRAMS.map((p, i) => (
              <StaggerItem key={p.slug} className={p.featured ? "md:col-span-2 lg:row-span-2 lg:col-span-1 md:row-span-1" : ""}>
                <Link
                  to="/programs"
                  data-testid={`program-card-${p.slug}`}
                  className={`group relative block w-full h-full rounded-2xl overflow-hidden lift ${p.featured ? "lg:h-full" : ""}`}
                >
                  <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-plum-dark/95 via-plum-dark/30 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <span className="text-gold-light text-[11px] tracking-[0.18em] uppercase font-semibold">{p.kicker}</span>
                    <h3 className="font-heading text-2xl sm:text-3xl text-cream font-semibold mt-1">{p.name}</h3>
                    <p className="text-cream/80 text-sm mt-2 max-w-md opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">{p.summary}</p>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* FOSTER CARE HOME SPOTLIGHT */}
      <section className="py-24 sm:py-32 bg-plum-dark text-cream relative z-10 overflow-hidden" data-testid="grouphome-spotlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <span className="text-gold-light text-sm tracking-[0.22em] uppercase font-semibold">Urgent Project</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 leading-tight">
              We got the keys. Now let's finish the home.
            </h2>
            <p className="text-cream/80 text-lg mt-6 leading-relaxed">
              Pastor Jewel and Elder Jones received the keys to a property that will become a safe, nurturing home for children in the foster care program. It's acquired and in restoration &mdash; and we need partners to bring it to life.
            </p>
            <ul className="mt-6 space-y-3">
              {["Monetary donations to fund renovations", "Skilled volunteers & tradespeople", "Furniture, materials & in-kind gifts", "Experienced grant writers"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-cream/85"><HandHeart className="text-gold shrink-0 mt-0.5" size={20} />{t}</li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-4 mt-9">
              <DonateDialog defaultDesignation="Foster Care Home" trigger={
                <button data-testid="grouphome-donate-btn" className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-7 py-3.5 flex items-center gap-2 transition-all hover:-translate-y-1">
                  <Heart size={17} /> Fund the Restoration
                </button>
              } />
              <Link to="/get-involved" className="border border-cream/30 text-cream rounded-full px-7 py-3.5 hover:bg-cream/10 transition-colors flex items-center gap-2">Volunteer <ArrowRight size={17} /></Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMG.ghKey} alt="Pastor Jewel and her husband receiving the keys to the foster care home" className="col-span-2 rounded-2xl h-64 w-full object-cover shadow-2xl" />
              <img src={IMG.ghRoom} alt="Room awaiting restoration" className="rounded-2xl h-40 w-full object-cover" />
              <img src={IMG.ghPaint} alt="Volunteers restoring the home" className="rounded-2xl h-40 w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WAYS TO GIVE */}
      <section className="py-24 sm:py-32 relative z-10" data-testid="ways-to-give">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading kicker="Ways to Partner" title="More than money — every gift moves the mission" align="center" />
          <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WAYS.map((w) => {
              const Inner = (
                <div className="h-full bg-white rounded-2xl border border-plum/10 p-7 lift flex flex-col">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl bg-plum/5 text-plum mb-5"><w.icon size={26} /></span>
                  <h3 className="font-heading text-2xl text-plum font-semibold">{w.title}</h3>
                  <p className="text-stone text-sm mt-2 leading-relaxed flex-1">{w.desc}</p>
                  <span className="text-gold-dark text-sm font-medium mt-4 flex items-center gap-2">Get started <ArrowRight size={15} /></span>
                </div>
              );
              return (
                <StaggerItem key={w.title}>
                  {w.cta === "donate" ? (
                    <DonateDialog trigger={<button className="w-full text-left h-full" data-testid="way-donate">{Inner}</button>} />
                  ) : (
                    <Link to={w.to} className="block h-full" data-testid={`way-${w.title.toLowerCase().split(" ")[1]}`}>{Inner}</Link>
                  )}
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* TESTIMONIAL TEASER */}
      {tItems[0] && (
        <section className="pb-24 sm:pb-32 relative z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal className="bg-plum rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
              <Quote className="text-gold/30 mx-auto mb-6" size={48} />
              <p className="font-heading text-2xl sm:text-3xl text-cream leading-snug italic">&ldquo;{tItems[0].quote}&rdquo;</p>
              <p className="text-gold-light font-semibold mt-6">{tItems[0].name || tItems[0].role}</p>
              <p className="text-cream/60 text-sm">{tItems[0].name ? `${tItems[0].role} · ${tItems[0].program}` : tItems[0].program}</p>
              <Link to="/testimonials" className="inline-flex items-center gap-2 text-cream/80 hover:text-gold mt-8 text-sm font-medium" data-testid="home-testimonials-link">
                Read more stories <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
        </section>
      )}
    </>
  );
}
